import {
    BookOpen,
    BotMessageSquare,
    BrainCircuit,
    GitGraph,
    HandCoins,
    LayoutDashboard,
    Lock,
    MessageSquareCode,
    MessagesSquare,
    Package,
    Receipt,
    Store,
    Ticket,
} from "lucide-react";
import * as React from "react";

import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarRail,
} from "@/components/ui/sidebar";

import { NavMain } from "./nav-main";
import { NavUser } from "./nav-user";
import useGlobalPermission from "@/store/user-permissions";
import { useCheckUser } from "@/api/user/check-user";

type Permission = {
    module: Module;
    user_id: string;
    can_get_list: boolean;
    can_get_one: boolean;
    can_create: boolean;
    can_update: boolean;
    can_delete: boolean;
};

type Module = {
    id: string;
    name: string;
};

// This is sample data.
// const data = {
//     user: {
//         name: "App User",
//         email: "m@example.com",
//         avatar: "/avatars/shadcn.jpg",
//     },
//     team: {
//         name: "Shoe AI",
//         logo: Footprints,
//         plan: "Chatbot",
//     },

//     navMain: [
//         {
//             title: "Dashboard",
//             url: "/app/dashboard",
//             icon: LayoutDashboard,
//             isActive: true,
//         },
//         {
//             title: "Conversations",
//             url: "/app/conversations?type=all",
//             icon: MessagesSquare,
//         },
//         {
//             title: "Leads",
//             url: "/app/leads",
//             icon: BookOpen,
//         },
//         {
//             title: "Proactive Chats",
//             url: "/app/proactive-chats",
//             icon: BotMessageSquare,
//         },
//         {
//             title: "AI Training",
//             url: "/app/ai-training",
//             icon: BrainCircuit,
//         },
//         {
//             title: "Chat Settings",
//             url: "/app/chat-settings",
//             icon: MessageSquareCode,
//         },
//         {
//             title: "Refund and Replacement Request",
//             url: "/app/refund-request",
//             icon: HandCoins,
//         },
//         // {
//         //   title: 'Demo Menu',
//         //   url: '#',
//         //   icon: FlaskConical,
//         //   items: [
//         //     {
//         //       title: 'General',
//         //       url: '#',
//         //     },
//         //     {
//         //       title: 'Team',
//         //       url: '#',
//         //     },
//         //     {
//         //       title: 'Billing',
//         //       url: '#',
//         //     },
//         //     {
//         //       title: 'Limits',
//         //       url: '#',
//         //     },
//         //   ],
//         // },
//     ],
// };

const sidebarListGen = (permissionList: Permission[], userRole?: string) => {
    const finalList = [];
    const dashboardPermission = permissionList.find((item: Permission) => item.module.name === "Dashboard");

    if (
        dashboardPermission?.can_get_list ||
        dashboardPermission?.can_get_one ||
        dashboardPermission?.can_create ||
        dashboardPermission?.can_update ||
        dashboardPermission?.can_delete
    ) {
        finalList.push({
            title: "Dashboard",
            url: "/app/dashboard",
            icon: LayoutDashboard,
        });
    }

    const conversationPermission = permissionList.find((item) => item.module.name === "Conversation");

    if (
        conversationPermission?.can_get_list ||
        conversationPermission?.can_get_one ||
        conversationPermission?.can_create ||
        conversationPermission?.can_update ||
        conversationPermission?.can_delete
    ) {
        finalList.push({
            title: "Conversations",
            url: "/app/conversations?type=all",
            icon: MessagesSquare,
        });
    }

    // Only show Store Management menu if user is super_admin
    if (userRole === "super_admin") {
        finalList.push({
            title: "Store Management",
            url: "/app/store-management",
            icon: Store,
        });
    }

    // Only show Billing menu if user is super_admin
    if (userRole === "super_admin") {
        finalList.push({
            title: "Billing",
            url: "/app/billing",
            icon: Receipt,
        });
    }

    // Only show Packages menu if user is super_admin
    if (userRole === "super_admin") {
        finalList.push({
            title: "Packages",
            url: "/app/packages",
            icon: Package,
        });
    }

    finalList.push({
        title: "Tickets",
        url: "/app/tickets",
        icon: Ticket,
    });

    // Only show Store Access menu if user is super_admin
    // if (userRole === "super_admin") {
    //     finalList.push({
    //         title: "Store Access",
    //         url: "/app/store-access",
    //         icon: Key,
    //     });
    // }
    finalList.push({
        title: "Reports",
        url: "/app/admin-reports",
        icon: GitGraph,
    });

    // Only show Access Control menu if user is super_admin
    if (userRole === "super_admin") {
        finalList.push({
            title: "Access Control",
            url: "/app/access-control",
            icon: Lock,
        });
    }

    // finalList.push({
    //     title: "System Settings",
    //     url: "/app/system-settings",
    //     icon: Settings,
    // });

    const leadsPermission = permissionList.find((item) => item.module.name === "Leads");

    if (
        leadsPermission?.can_get_list ||
        leadsPermission?.can_get_one ||
        leadsPermission?.can_create ||
        leadsPermission?.can_update ||
        leadsPermission?.can_delete
    ) {
        finalList.push({
            title: "Leads",
            url: "/app/leads",
            icon: BookOpen,
        });
    }

    const proactivePermission = permissionList.find((item) => item.module.name === "Proactive_Chat");

    if (
        proactivePermission?.can_get_list ||
        proactivePermission?.can_get_one ||
        proactivePermission?.can_create ||
        proactivePermission?.can_update ||
        proactivePermission?.can_delete
    ) {
        finalList.push({
            title: "Proactive Chats",
            url: "/app/proactive-chats",
            icon: BotMessageSquare,
        });
    }

    const trainingPermission = permissionList.find((item) => item.module.name === "AI_Training");

    if (
        trainingPermission?.can_get_list ||
        trainingPermission?.can_get_one ||
        trainingPermission?.can_create ||
        trainingPermission?.can_update ||
        trainingPermission?.can_delete
    ) {
        finalList.push({
            title: "AI Training",
            url: "/app/ai-training",
            icon: BrainCircuit,
        });
    }

    const chatSettingsPermission = permissionList.find((item) => item.module.name === "Chat_Settings");

    if (
        chatSettingsPermission?.can_get_list ||
        chatSettingsPermission?.can_get_one ||
        chatSettingsPermission?.can_create ||
        chatSettingsPermission?.can_update ||
        chatSettingsPermission?.can_delete
    ) {
        finalList.push({
            title: "Chat Settings",
            url: "/app/chat-settings",
            icon: MessageSquareCode,
        });
    }

    const refundPermission = permissionList.find((item) => item.module.name === "Order_Refund_Requests");

    if (
        refundPermission?.can_get_list ||
        refundPermission?.can_get_one ||
        refundPermission?.can_create ||
        refundPermission?.can_update ||
        refundPermission?.can_delete
    ) {
        finalList.push({
            title: "Refund and Replacement Request",
            url: "/app/refund-request",
            icon: HandCoins,
        });
    }

    return finalList;
};

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
    const { permissionList } = useGlobalPermission();
    const { data: userData } = useCheckUser();
    const userRole = userData?.data?.user?.role;

    return (
        <Sidebar collapsible="icon" {...props}>
            <SidebarHeader>
                <SidebarMenu>
                    <SidebarMenuItem>
                        <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
                            <img src="/assets/images/logo/shoe-ai-logo.png" alt="logo" width={200} height={40} className="h-8 w-24" />
                        </SidebarMenuButton>
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarHeader>
            <SidebarContent>
                <NavMain items={sidebarListGen(permissionList, userRole)} />
                {/* <NavMain items={data.navMain} /> */}
            </SidebarContent>
            <SidebarFooter>
                <NavUser />
            </SidebarFooter>
            <SidebarRail />
        </Sidebar>
    );
}
